import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { reservationAPI } from '../api/axios';
import ReservationDetailModal from '../components/ReservationDetailModal';
import './ReservationManagementPage.css';

/**
 * 예약 관리 페이지 (본문 컨텐츠만)
 * 사이드바와 헤더는 AdminLayout에서 제공
 */
function ReservationManagementPage() {
  const location = useLocation();
  const [view, setView] = useState('dayGridMonth');
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);

  // URL에서 날짜 파라미터 확인 (대시보드에서 넘어온 경우)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateParam = params.get('date');
    
    if (dateParam) {
      setSelectedDate(dateParam);
      setView('timeGridDay');
    }
  }, [location]);

  // 예약 데이터 로드
  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      setIsLoading(true);
      const response = await reservationAPI.getAll();
      setEvents(response.data);
    } catch (error) {
      console.error('예약 데이터 로드 실패:', error);
      alert('예약 데이터를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEventClick = (clickInfo) => {
    // FullCalendar 이벤트 객체에서 extendedProps로 접근
    const eventData = clickInfo.event;
    
    const reservation = {
      id: eventData.id,
      title: eventData.title,
      start: eventData.start,
      end: eventData.end,
      backgroundColor: eventData.backgroundColor,
      borderColor: eventData.borderColor,
      // extendedProps에 저장된 추가 정보
      customerName: eventData.extendedProps.customerName,
      customerPhone: eventData.extendedProps.customerPhone,
      treatment: eventData.extendedProps.treatment,
      status: eventData.extendedProps.status,
      depositAmount: eventData.extendedProps.depositAmount,
      reservationTime: eventData.extendedProps.reservationTime,
    };
    
    setSelectedReservation(reservation);
    setIsModalOpen(true);
  };

  const handleStatusChange = async (reservationId, newStatus) => {
    try {
      await reservationAPI.updateStatus(reservationId, newStatus);

      // 성공 시 데이터 새로고침
      await fetchReservations();
      alert('예약 상태가 변경되었습니다.');
    } catch (error) {
      console.error('상태 변경 실패:', error);
      throw error;
    }
  };

  const handleDelete = async (reservationId) => {
    try {
      await reservationAPI.delete(reservationId);
      
      // 성공 시 데이터 새로고침
      await fetchReservations();
      alert('예약이 삭제되었습니다.');
    } catch (error) {
      console.error('예약 삭제 실패:', error);
      throw error;
    }
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  return (
    <div className="reservation-management-container">
      {/* View Switcher */}
      <div className="view-switcher-section">
        <div className="view-switcher">
          <button 
            className={`view-button ${view === 'dayGridMonth' ? 'active' : ''}`}
            onClick={() => handleViewChange('dayGridMonth')}
          >
            월간
          </button>
          <button 
            className={`view-button ${view === 'timeGridWeek' ? 'active' : ''}`}
            onClick={() => handleViewChange('timeGridWeek')}
          >
            주간
          </button>
          <button 
            className={`view-button ${view === 'timeGridDay' ? 'active' : ''}`}
            onClick={() => handleViewChange('timeGridDay')}
          >
            일간
          </button>
        </div>
      </div>

      {/* Calendar Content */}
      <div className="reservation-content">
        {isLoading ? (
          <div className="loading-message">
            <p>예약 데이터를 불러오는 중...</p>
          </div>
        ) : (
          <>
            <div className="calendar-container">
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView={view}
                initialDate={selectedDate || new Date()}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: ''
                }}
                buttonText={{
                  today: '오늘',
                  month: '월',
                  week: '주',
                  day: '일'
                }}
                locale="ko"
                events={events}
                eventClick={handleEventClick}
                editable={false}
                selectable={true}
                selectMirror={true}
                dayMaxEvents={true}
                weekends={true}
                slotMinTime="09:00:00"
                slotMaxTime="20:00:00"
                allDaySlot={false}
                height="auto"
                contentHeight="auto"
                aspectRatio={1.8}
                views={{
                  timeGridWeek: {
                    titleFormat: { year: 'numeric', month: 'long', day: 'numeric' }
                  },
                  timeGridDay: {
                    titleFormat: { year: 'numeric', month: 'long', day: 'numeric' }
                  }
                }}
              />
            </div>

            {/* Legend */}
            <div className="calendar-legend">
              <h3 className="legend-title">예약 상태</h3>
              <div className="legend-items">
                <div className="legend-item">
                  <span className="legend-color" style={{backgroundColor: '#FF9800'}}></span>
                  <span className="legend-text">예약 대기</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{backgroundColor: '#4CAF50'}}></span>
                  <span className="legend-text">예약 확정</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{backgroundColor: '#9E9E9E'}}></span>
                  <span className="legend-text">시술 완료</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{backgroundColor: '#F44336'}}></span>
                  <span className="legend-text">예약 취소</span>
                </div>
                <div className="legend-item">
                  <span className="legend-color" style={{backgroundColor: '#D32F2F'}}></span>
                  <span className="legend-text">노쇼</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Reservation Detail Modal */}
      <ReservationDetailModal
        reservation={selectedReservation}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedReservation(null);
        }}
        onStatusChange={handleStatusChange}
        onDelete={handleDelete}
      />
    </div>
  );
}

export default ReservationManagementPage;
